import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, Hash, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import OtpModal from '../components/OtpModal';
import brandLogo from '../assets/aurora-wealth-reserves-logo.png';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    account_number: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpData, setOtpData] = useState(null);
  const [suspensionData, setSuspensionData] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) setError('');
    if (validationErrors[e.target.name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[e.target.name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setValidationErrors({});
    setSuspensionData(null);

    try {
      const result = await login(formData);
      
      if (result.success) {
        navigate('/dashboard');
      } else if (result.requiresOtp) {
        // Show OTP modal
        setOtpData({
          accountNumber: formData.account_number,
          otpMethod: result.otpData.otp_method,
          expiresAt: result.otpData.expires_at
        });
        setShowOtpModal(true);
        setError('');
      } else if (result.isSuspended) {
        // Handle suspension
        setSuspensionData(result.suspensionData);
        setError(result.error);
      } else {
        setError(result.error);
        if (result.errors) {
          setValidationErrors(result.errors);
        }
      }
    } catch (error) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSuccess = () => {
    setShowOtpModal(false);
    setOtpData(null);
    navigate('/dashboard');
  };

  const handleOtpClose = () => {
    setShowOtpModal(false);
    setOtpData(null);
  };

  return (
    <>
      {/* OTP Modal */}
      <OtpModal 
        isOpen={showOtpModal}
        onClose={handleOtpClose}
        accountNumber={otpData?.accountNumber}
        otpMethod={otpData?.otpMethod}
        expiresAt={otpData?.expiresAt}
        onSuccess={handleOtpSuccess}
      />
      
      {/* Main Login Form */}
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-primary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="max-w-md w-full space-y-8"
      >
        <div>
          <div className="mx-auto flex justify-center">
            <img
              src={brandLogo}
              alt="Aurora Wealth Reserves"
              className="h-20 sm:h-24 w-auto max-w-[280px] object-contain"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900 font-sans">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Welcome back to Aurora Wealth Reserves
          </p>
        </div>

        <motion.form
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mt-8 space-y-6"
          onSubmit={handleSubmit}
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`border rounded-lg p-4 flex items-start space-x-3 ${
                suspensionData 
                  ? 'bg-orange-50 border-orange-200' 
                  : 'bg-red-50 border-red-200'
              }`}
            >
              <AlertCircle className={`h-5 w-5 flex-shrink-0 ${
                suspensionData ? 'text-orange-500' : 'text-red-500'
              }`} />
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  suspensionData ? 'text-orange-700' : 'text-red-700'
                }`}>
                  {error}
                </p>
                {suspensionData && (
                  <div className="mt-3 space-y-2">
                    <div className="text-sm text-orange-600">
                      <p><strong>Details:</strong> {suspensionData.reason}</p>
                      {suspensionData.expires_at && !suspensionData.is_permanent && (
                        <p><strong>Access will be restored on:</strong> {new Date(suspensionData.expires_at).toLocaleDateString()}</p>
                      )}
                    </div>
                    <div className="text-xs text-orange-500 bg-orange-100 rounded p-2">
                      <p>If you believe this suspension is in error, please contact customer support.</p>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {Object.keys(validationErrors).length > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-red-50 border border-red-200 rounded-lg p-4"
            >
              <div className="flex items-center space-x-3 mb-3">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                <p className="text-sm font-medium text-red-700">Please fix the following errors:</p>
              </div>
              <ul className="space-y-1">
                {Object.entries(validationErrors).map(([field, errors]) => (
                  <li key={field} className="text-sm text-red-600">
                    <span className="font-medium capitalize">{field.replace(/_/g, ' ')}:</span> {Array.isArray(errors) ? errors[0] : errors}
                  </li>
                ))}
              </ul>
            </motion.div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="account_number" className="block text-sm font-medium text-gray-700 mb-2">
                Account Number
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Hash className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="account_number"
                  name="account_number"
                  type="text"
                  autoComplete="username"
                  required
                  value={formData.account_number}
                  onChange={handleChange}
                  maxLength="20"
                  pattern="[0-9]{10,}"
                  className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                    validationErrors.account_number ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your account number (10+ digits)"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-10 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                    validationErrors.password ? 'border-red-300 focus:ring-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <Link
                to="/forgot-password"
                className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
              >
                Forgot your password?
              </Link>
            </div>
          </div>

          <div>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </motion.button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link
                to="/register"
                className="font-medium text-primary-600 hover:text-primary-500 transition-colors"
              >
                Open account
              </Link>
            </p>
          </div>
        </motion.form>
      </motion.div>
    </div>
    </>
  );
};

export default Login; 